<template>
    <main class="g11c" dir="rtl" lang="ar">
        <nav class="g11c-breadcrumbs g11c-wrap" aria-label="مسار الصفحة">
            <ol class="g11c-breadcrumbs__list">
                <li>
                    <router-link :to="config.homeRoute" @click.native="onInternal('home', 'breadcrumb')">
                        الرئيسية
                    </router-link>
                </li>
                <li>
                    <router-link :to="config.mainGrade11CourseRoute" @click.native="onTrack('seo_grade11_month1_click', 'breadcrumb', config.mainGrade11CourseUrl)">
                        تانية ثانوي
                    </router-link>
                </li>
                <li aria-current="page">منهج البرمجة والذكاء الاصطناعي</li>
            </ol>
        </nav>

        <!-- Hero -->
        <section class="g11c-hero">
            <div class="g11c-wrap g11c-hero__grid">
                <div class="g11c-hero__intro">
                    <div class="g11c-hero__content">
                        <span class="g11c-badge">منهج تانية ثانوي بكالوريا {{ config.academicYear }}</span>
                        <h1 class="g11c-hero__h1">
                            منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا {{ config.academicYear }}
                        </h1>
                        <p class="g11c-hero__lead">مش مجرد مادة برمجة… دي بداية لفهم التكنولوجيا الحديثة</p>
                        <p class="g11c-hero__text">
                            منهج البرمجة والذكاء الاصطناعي لتانية ثانوي بياخدك في رحلة تبدأ من فهم الأنظمة الرقمية، وتمر بأمن المعلومات وتطبيقات الويب وتصميم المواقع وتحليل البيانات، لحد ما توصل إلى
                            <span class="ltr-inline">Machine Learning</span>
                            و
                            <span class="ltr-inline">Generative AI</span>.
                            المنهج بيجمع بين الجانب النظري والتفكير العملي، وبيساعدك تفهم التكنولوجيا بدل ما تكون مجرد مستخدم لها.
                        </p>
                        <div class="g11c-hero__chips">
                            <span v-for="chip in heroChips" :key="chip.label" class="g11c-chip">
                                <lucide-icon :name="chip.icon" :size="15" aria-hidden="true" />
                                {{ chip.label }}
                            </span>
                        </div>
                        <a
                            href="#downloads"
                            class="g11c-book-banner"
                            @click.prevent="scrollToDownloads('hero')"
                        >
                            <lucide-icon name="BookMarked" :size="22" aria-hidden="true" />
                            <span>
                                <strong>كتاب البرمجة الرسمي للوزارة</strong>
                                عربي ولغات · الترم الأول والثاني · PDF مجاني
                            </span>
                        </a>
                    </div>

                    <aside class="g11c-hero__visual" aria-label="خريطة رحلة منهج البرمجة والذكاء الاصطناعي">
                        <a
                            href="#chapters"
                            class="g11c-hero__visual-link"
                            @click.prevent="scrollToChapters"
                        >
                            <img
                                class="g11c-hero__visual-img"
                                :src="journeyBanner.src"
                                :alt="journeyBanner.alt"
                                :width="journeyBanner.width"
                                :height="journeyBanner.height"
                                loading="eager"
                                decoding="async"
                            />
                        </a>
                        <p class="g11c-hero__visual-hint">
                            <lucide-icon name="ChevronsDown" :size="15" aria-hidden="true" />
                            اضغط لاستكشاف الفصول السبعة
                        </p>
                    </aside>
                </div>

                <div class="g11c-cta-pair g11c-hero__ctas">
                    <div class="g11c-cta-card g11c-cta-card--primary">
                        <div class="g11c-cta-card__head">
                            <div class="g11c-cta-card__icon" aria-hidden="true">
                                <lucide-icon name="Gift" :size="22" />
                            </div>
                            <div>
                                <p class="g11c-cta-card__eyebrow">مجاني بالكامل</p>
                                <p class="g11c-cta-card__title">الكورس التمهيدي</p>
                            </div>
                        </div>
                        <router-link
                            :to="config.introCourseRoute"
                            class="g11c-btn g11c-btn--primary"
                            @click.native="onTrack('seo_grade11_intro_course_click', 'hero', config.introCourseUrl)"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            <span class="g11c-btn__label g11c-btn__label--full">ابدأ الكورس التمهيدي مجانًا</span>
                            <span class="g11c-btn__label g11c-btn__label--short">ابدأ مجانًا</span>
                        </router-link>
                        <ul class="g11c-cta-points" aria-label="مميزات الكورس التمهيدي">
                            <li>
                                <lucide-icon name="CheckCircle2" :size="16" aria-hidden="true" />
                                <span>راجع أهم أساسيات أولى ثانوي قبل المنهج</span>
                            </li>
                            <li>
                                <lucide-icon name="CheckCircle2" :size="16" aria-hidden="true" />
                                <span>تأسيس ومفاهيم وطريقة التفكير في الأسئلة</span>
                            </li>
                        </ul>
                    </div>
                    <div class="g11c-cta-card g11c-cta-card--secondary">
                        <div class="g11c-cta-card__head">
                            <div class="g11c-cta-card__icon g11c-cta-card__icon--dark" aria-hidden="true">
                                <lucide-icon name="BookOpen" :size="22" />
                            </div>
                            <div>
                                <p class="g11c-cta-card__eyebrow">ابدأ المنهج</p>
                                <p class="g11c-cta-card__title">كورس الشهر الأول</p>
                            </div>
                        </div>
                        <router-link
                            :to="config.monthOneCourseRoute"
                            class="g11c-btn g11c-btn--secondary"
                            @click.native="onTrack('seo_grade11_month1_click', 'hero', config.monthOneCourseUrl)"
                        >
                            <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                            <span class="g11c-btn__label g11c-btn__label--full">اشترك في كورس الشهر الأول</span>
                            <span class="g11c-btn__label g11c-btn__label--short">اشترك الآن</span>
                        </router-link>
                        <ul class="g11c-cta-points" aria-label="مميزات كورس الشهر الأول">
                            <li>
                                <lucide-icon name="CheckCircle2" :size="16" aria-hidden="true" />
                                <span>ابدأ المنهج من البداية بشرح وتدريبات</span>
                            </li>
                            <li>
                                <lucide-icon name="CheckCircle2" :size="16" aria-hidden="true" />
                                <span>Quizzes ومتابعة منظمة طول الشهر</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <!-- Intro video -->
        <section class="g11c-section g11c-section--video" aria-labelledby="g11c-video-title">
            <div class="g11c-wrap">
                <header class="g11c-video__header">
                    <h2 id="g11c-video-title" class="g11c-h2">{{ introVideo.title }}</h2>
                    <p class="g11c-text">{{ introVideo.subtitle }}</p>
                </header>
                <div class="g11c-video">
                    <div class="g11c-video__frame">
                        <iframe
                            class="g11c-video__iframe"
                            :src="youtubeEmbedUrl"
                            title="فيديو تعريفي — منهج البرمجة والذكاء الاصطناعي تانية ثانوي | البرمجة مع غنيم"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowfullscreen
                            loading="lazy"
                            referrerpolicy="strict-origin-when-cross-origin"
                        />
                    </div>
                    <a
                        class="g11c-video__open"
                        :href="introVideo.watchUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click="onTrack('seo_grade11_intro_video_click', 'intro_video', introVideo.watchUrl)"
                    >
                        <lucide-icon name="Youtube" :size="18" aria-hidden="true" />
                        مشاهدة على يوتيوب
                    </a>
                </div>
            </div>
        </section>

        <!-- Official ministry books -->
        <section id="downloads" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap">
                <h2 class="g11c-h2 g11c-h2--center">تحميل كتاب البرمجة الرسمي للوزارة PDF</h2>
                <p class="g11c-lead g11c-lead--center">
                    كتاب الوزارة لتانية ثانوي بكالوريا {{ config.academicYear }} — نسخة عربي ونسخة لغات، الترم الأول والترم الثاني.
                </p>
                <div
                    v-for="group in downloadBookGroups"
                    :key="group.id"
                    class="g11c-download-group"
                >
                    <h3 class="g11c-download-group__title">{{ group.title }}</h3>
                    <p class="g11c-download-group__subtitle">{{ group.subtitle }}</p>
                    <div class="g11c-download-grid">
                        <article
                            v-for="book in group.books"
                            :key="book.id"
                            class="g11c-download-card"
                            :class="{ 'g11c-download-card--en': book.language === 'en' }"
                        >
                            <div class="g11c-download-card__head">
                                <lucide-icon
                                    :name="book.language === 'ar' ? 'BookMarked' : 'Languages'"
                                    :size="28"
                                    aria-hidden="true"
                                />
                                <h4
                                    class="g11c-download-card__title"
                                    :class="{ 'ltr-inline': book.language === 'en' }"
                                >
                                    {{ book.title }}
                                </h4>
                            </div>
                            <ul class="g11c-download-card__meta">
                                <li v-for="detail in book.details" :key="detail">{{ detail }}</li>
                                <li>{{ book.fileSize }}</li>
                            </ul>
                            <div class="g11c-download-card__actions">
                                <a
                                    :href="book.url"
                                    class="g11c-btn g11c-btn--primary"
                                    :download="book.fileName"
                                    @click="onBookTrack(book.trackDownload, 'downloads', book.url)"
                                >
                                    <lucide-icon name="Download" :size="18" aria-hidden="true" />
                                    <span>{{ book.downloadLabel }}</span>
                                </a>
                                <a
                                    :href="book.url"
                                    class="g11c-btn g11c-btn--ghost"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    @click="onBookTrack(book.trackOpen, 'downloads', book.url)"
                                >
                                    <lucide-icon name="ExternalLink" :size="18" aria-hidden="true" />
                                    <span>{{ book.openLabel }}</span>
                                </a>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </section>

        <!-- Overview -->
        <section id="overview" class="g11c-section">
            <div class="g11c-wrap g11c-section__narrow">
                <h2 class="g11c-h2">ما هو منهج البرمجة تانية ثانوي بكالوريا؟</h2>
                <p class="g11c-text">
                    منهج البرمجة والذكاء الاصطناعي للصف الثاني الثانوي يتكوّن من 7 فصول مترابطة.
                    كل فصل يشرح جانبًا مختلفًا من عالم التكنولوجيا، لكن الفصول كلها في النهاية بتكوّن صورة متكاملة لنظام رقمي حديث.
                </p>
                <p class="g11c-text g11c-text--strong">الطالب هيتعلم:</p>
                <ul class="g11c-checklist">
                    <li v-for="item in overviewPoints" :key="item">{{ item }}</li>
                </ul>
            </div>

            <div class="g11c-wrap">
                <nav class="g11c-toc" aria-label="فصول المنهج">
                    <div class="g11c-toc__header">
                        <div class="g11c-toc__header-icon" aria-hidden="true">
                            <lucide-icon name="ListTree" :size="22" />
                        </div>
                        <div>
                            <p class="g11c-toc__title">انتقل إلى فصل</p>
                            <p class="g11c-toc__subtitle">7 فصول مترابطة — اضغط للانتقال مباشرة إلى أي فصل</p>
                        </div>
                    </div>
                    <div class="g11c-toc__grid">
                        <a
                            v-for="chapter in chapters"
                            :key="`toc-${chapter.id}`"
                            :href="`#${chapter.id}`"
                            class="g11c-toc__card"
                            @click="onChapterNav(chapter)"
                        >
                            <span class="g11c-toc__num">{{ chapter.number }}</span>
                            <span class="g11c-toc__icon" aria-hidden="true">
                                <lucide-icon :name="chapter.icon" :size="20" />
                            </span>
                            <span class="g11c-toc__text">
                                <span class="g11c-toc__name">{{ chapter.arabicTitle }}</span>
                                <span class="g11c-toc__en ltr-inline">{{ chapter.englishTitle }}</span>
                            </span>
                            <span class="g11c-toc__chevron" aria-hidden="true">
                                <lucide-icon name="ChevronLeft" :size="18" />
                            </span>
                        </a>
                    </div>
                </nav>

                <div class="g11c-cta-pair g11c-overview-ctas">
                    <div class="g11c-cta-card g11c-cta-card--primary">
                        <div class="g11c-cta-card__head">
                            <div class="g11c-cta-card__icon" aria-hidden="true">
                                <lucide-icon name="Gift" :size="20" />
                            </div>
                            <div>
                                <p class="g11c-cta-card__eyebrow">مجاني</p>
                                <p class="g11c-cta-card__title">الكورس التمهيدي</p>
                            </div>
                        </div>
                        <router-link
                            :to="config.introCourseRoute"
                            class="g11c-btn g11c-btn--primary"
                            @click.native="onTrack('seo_grade11_intro_course_click', 'after_summary', config.introCourseUrl)"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            <span>ابدأ مجانًا</span>
                        </router-link>
                    </div>
                    <div class="g11c-cta-card g11c-cta-card--secondary">
                        <div class="g11c-cta-card__head">
                            <div class="g11c-cta-card__icon g11c-cta-card__icon--dark" aria-hidden="true">
                                <lucide-icon name="BookOpen" :size="20" />
                            </div>
                            <div>
                                <p class="g11c-cta-card__eyebrow">ابدأ المنهج</p>
                                <p class="g11c-cta-card__title">كورس الشهر الأول</p>
                            </div>
                        </div>
                        <router-link
                            :to="config.monthOneCourseRoute"
                            class="g11c-btn g11c-btn--secondary"
                            @click.native="onTrack('seo_grade11_month1_click', 'after_summary', config.monthOneCourseUrl)"
                        >
                            <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                            <span>اشترك الآن</span>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>

        <!-- Chapters -->
        <section id="chapters" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap">
                <h2 class="g11c-h2 g11c-h2--center">فصول منهج البرمجة والذكاء الاصطناعي</h2>
                <p class="g11c-lead g11c-lead--center">
                    سبعة فصول مترابطة في منهج البرمجة تانية ثانوي بكالوريا — من الأنظمة الرقمية إلى الذكاء الاصطناعي.
                </p>
                <figure
                    v-if="curriculumMap"
                    class="g11c-curriculum-map"
                    itemscope
                    itemtype="https://schema.org/ImageObject"
                >
                    <button
                        type="button"
                        class="g11c-curriculum-map__trigger"
                        :aria-label="`تكبير ${curriculumMap.shortLabel}`"
                        @click="openMapModal"
                    >
                        <img
                            :src="curriculumMap.src"
                            :alt="curriculumMap.alt"
                            :title="curriculumMap.title"
                            :width="curriculumMap.width"
                            :height="curriculumMap.height"
                            loading="lazy"
                            decoding="async"
                            class="g11c-curriculum-map__img"
                            itemprop="contentUrl"
                        />
                    </button>
                    <figcaption class="g11c-curriculum-map__caption" itemprop="caption">
                        {{ curriculumMap.caption }}
                    </figcaption>
                    <meta itemprop="name" :content="curriculumMap.label" />
                    <meta itemprop="description" :content="curriculumMap.alt" />
                </figure>
            </div>
            <curriculum-chapter
                v-for="(chapter, index) in chapters"
                :key="chapter.id"
                :chapter="chapter"
                :alternate="index % 2 === 1"
            />
            <div class="g11c-wrap">
                <div class="g11c-cta-pair g11c-cta-pair--center">
                    <router-link
                        :to="config.introCourseRoute"
                        class="g11c-btn g11c-btn--primary"
                        @click.native="onTrack('seo_grade11_intro_course_click', 'after_chapters', config.introCourseUrl)"
                    >
                        <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                        <span>ابدأ الكورس التمهيدي مجانًا</span>
                    </router-link>
                    <router-link
                        :to="config.monthOneCourseRoute"
                        class="g11c-btn g11c-btn--secondary"
                        @click.native="onTrack('seo_grade11_month1_click', 'after_chapters', config.monthOneCourseUrl)"
                    >
                        <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                        <span>اشترك في كورس الشهر الأول</span>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- System flow -->
        <section id="system-flow" class="g11c-section">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">كيف ترتبط فصول المنهج ببعضها؟</h2>
                <p class="g11c-text">تخيّل إنك بتبني منصة تعليمية ذكية.</p>
                <div class="g11c-flow">
                    <div v-for="(step, index) in systemFlow" :key="step.title" class="g11c-flow__card">
                        <span class="g11c-flow__num">{{ index + 1 }}</span>
                        <h3 class="g11c-flow__title">{{ step.title }}</h3>
                        <p>{{ step.text }}</p>
                    </div>
                </div>
                <p class="g11c-text g11c-text--strong">
                    الفصول السبعة مش موضوعات منفصلة. هي أجزاء بتتجمع مع بعض عشان تكوّن نظامًا رقميًا متكاملًا.
                </p>
            </div>
        </section>

        <!-- Benefits -->
        <section id="benefits" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">ماذا سيستفيد الطالب من منهج تانية ثانوي؟</h2>
                <div class="g11c-cards g11c-cards--3">
                    <div v-for="benefit in benefits" :key="benefit" class="g11c-card">
                        <lucide-icon name="CheckCircle2" :size="22" class="g11c-card__icon" aria-hidden="true" />
                        <p>{{ benefit }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Career fields -->
        <section id="fields" class="g11c-section">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">منهج البرمجة يفتح لك أبوابًا مختلفة</h2>
                <p class="g11c-text">
                    المادة بتقدم للطالب مقدمة في مجموعة من أهم المجالات التقنية.
                </p>
                <div class="g11c-cards g11c-cards--fields">
                    <div v-for="field in careerFields" :key="field.title" class="g11c-field-card">
                        <lucide-icon :name="field.icon" :size="26" aria-hidden="true" />
                        <p class="g11c-field-card__ar">{{ field.label }}</p>
                        <p class="g11c-field-card__en ltr-inline">{{ field.title }}</p>
                    </div>
                </div>
                <p class="g11c-text g11c-text--strong">
                    مش مطلوب منك تختار تخصصك من دلوقتي. المطلوب إنك تفهم، تجرّب، وتكتشف المجال الأقرب ليك.
                </p>
            </div>
        </section>

        <!-- Theory vs practical -->
        <section id="theory-practical" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">هل منهج البرمجة تانية ثانوي نظري أم عملي؟</h2>
                <div class="g11c-split">
                    <div class="g11c-split__card">
                        <h3>الجزء النظري</h3>
                        <p>يساعدك تفهم المصطلحات والمفاهيم وطريقة عمل الأنظمة الحديثة.</p>
                    </div>
                    <div class="g11c-split__card g11c-split__card--accent">
                        <h3>الجزء العملي</h3>
                        <p>يساعدك تطبق الأفكار، وتحلل مواقف، وتفهم المواقع والبيانات، وتتعامل مع أمثلة ومشروعات مرتبطة بالتكنولوجيا.</p>
                    </div>
                </div>
                <p class="g11c-text">
                    داخل Coding With Ghonaim مش بنشرح المعلومة للحفظ فقط.
                    بنربطها بمثال، وتطبيق، وسؤال، وموقف من الحياة الحقيقية.
                </p>
            </div>
        </section>

        <!-- Preparation -->
        <section id="preparation" class="g11c-section">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">كيف تستعد لمنهج البرمجة تانية ثانوي؟</h2>
                <ol class="g11c-steps">
                    <li v-for="(step, index) in prepSteps" :key="step">
                        <span class="g11c-steps__num">{{ index + 1 }}</span>
                        <span>{{ step }}</span>
                    </li>
                </ol>
                <aside class="g11c-note">
                    مش لازم تكون محترف قبل ما تبدأ. المهم تراجع الأساس وتدخل المنهج بخطة واضحة.
                </aside>
                <router-link
                    :to="config.introCourseRoute"
                    class="g11c-btn g11c-btn--primary"
                    @click.native="onTrack('seo_grade11_intro_course_click', 'preparation', config.introCourseUrl)"
                >
                    <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                    <span>ابدأ الكورس التمهيدي مجانًا</span>
                </router-link>
            </div>
        </section>

        <!-- Free intro -->
        <section id="intro-course" class="g11c-section g11c-section--cta-band">
            <div class="g11c-wrap g11c-section__narrow">
                <p class="g11c-kicker">مش متأسس كويس من أولى ثانوي؟</p>
                <h2 class="g11c-h2 g11c-h2--light">ابدأ بالكورس التمهيدي المجاني</h2>
                <p class="g11c-text g11c-text--light">
                    لو نسيت جزءًا من منهج أولى ثانوي، أو حاسس إنك محتاج تراجع الأساس قبل بداية تانية ثانوي، ابدأ بالكورس التمهيدي.
                    الكورس يساعدك تراجع أهم الأفكار اللي محتاجها قبل الدخول في منهج الصف الثاني الثانوي.
                </p>
                <ul class="g11c-checklist g11c-checklist--light">
                    <li>أساسيات Programming &amp; AI.</li>
                    <li>أهم المفاهيم التقنية.</li>
                    <li>طريقة التفكير في الأسئلة.</li>
                    <li>أساسيات الجزء العملي.</li>
                    <li>الأفكار التي سيُبنى عليها منهج تانية ثانوي.</li>
                    <li>اختبار يساعدك تعرف مستوى استعدادك.</li>
                </ul>
                <div class="g11c-trust-badges">
                    <span>مجاني</span>
                    <span>عربي ولغات</span>
                    <span>مناسب قبل بداية المنهج</span>
                    <span>يبدأ من الأساس</span>
                </div>
                <router-link
                    :to="config.introCourseRoute"
                    class="g11c-btn g11c-btn--primary g11c-btn--on-dark"
                    @click.native="onTrack('seo_grade11_intro_course_click', 'intro_course_section', config.introCourseUrl)"
                >
                    <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                    <span>ابدأ الكورس التمهيدي مجانًا</span>
                </router-link>
                <p class="g11c-cta-note g11c-cta-note--light">سجّل على المنصة وابدأ المراجعة بدون دفع.</p>
                <p class="g11c-inline-link">
                    <router-link
                        :to="config.grade10CurriculumRoute"
                        @click.native="onTrack('seo_grade11_grade10_curriculum_click', 'intro_course_section', config.grade10CurriculumUrl)"
                    >
                        تعرف على منهج البرمجة أولى ثانوي
                    </router-link>
                </p>
            </div>
        </section>

        <!-- Month 1 -->
        <section id="month-one" class="g11c-section">
            <div class="g11c-wrap g11c-section__narrow">
                <p class="g11c-kicker g11c-kicker--dark">جاهز تبدأ منهج تانية ثانوي؟</p>
                <h2 class="g11c-h2">اشترك في كورس الشهر الأول</h2>
                <p class="g11c-text">
                    كورس الشهر الأول مناسب للطالب الجاهز يبدأ المنهج من البداية بخطة واضحة.
                </p>
                <ul class="g11c-checklist">
                    <li>شرح عربي ولغات.</li>
                    <li>شرح منظم للمفاهيم والمصطلحات.</li>
                    <li>تغطية الجزء النظري والعملي.</li>
                    <li>تدريبات وأسئلة متنوعة.</li>
                    <li>واجبات وQuizzes.</li>
                    <li>مراجعات واختبارات.</li>
                    <li>تطبيقات تساعدك تفهم الفكرة.</li>
                    <li>متابعة طوال فترة الدراسة.</li>
                    <li>خطة واضحة تمنع تراكم المنهج.</li>
                </ul>
                <div class="g11c-cta-pair">
                    <router-link
                        :to="config.monthOneCourseRoute"
                        class="g11c-btn g11c-btn--primary"
                        @click.native="onTrack('seo_grade11_month1_click', 'month1_section', config.monthOneCourseUrl)"
                    >
                        <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                        <span>اشترك في كورس الشهر الأول</span>
                    </router-link>
                    <router-link
                        :to="config.paymentInfoRoute"
                        class="g11c-btn g11c-btn--ghost"
                        @click.native="onTrack('seo_grade11_payment_info_click', 'month1_section', config.paymentInfoUrl)"
                    >
                        <lucide-icon name="CreditCard" :size="20" aria-hidden="true" />
                        <span>اعرف طرق الاشتراك والدفع</span>
                    </router-link>
                </div>
                <p class="g11c-inline-link">
                    <router-link
                        :to="config.mainGrade11CourseRoute"
                        @click.native="onTrack('seo_grade11_month1_click', 'month1_section_main', config.mainGrade11CourseUrl)"
                    >
                        كورس البرمجة تانية ثانوي
                    </router-link>
                </p>
            </div>
        </section>

        <!-- Method -->
        <section id="method" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap">
                <h2 class="g11c-h2">لماذا تدرس مع Coding With Ghonaim؟</h2>
                <p class="g11c-lead">بنفهم المنهج… وبنحوّله لمهارة</p>
                <p class="g11c-text">في Coding With Ghonaim بنساعد الطالب يحقق هدفين معًا.</p>
                <div class="g11c-split">
                    <div class="g11c-split__card">
                        <h3>الهدف الأول</h3>
                        <p>يفهم منهج البرمجة والذكاء الاصطناعي ويستعد للامتحان والدرجة.</p>
                    </div>
                    <div class="g11c-split__card g11c-split__card--accent">
                        <h3>الهدف الثاني</h3>
                        <p>يربط اللي بيدرسه بالتكنولوجيا الحقيقية والمجالات الموجودة حوله.</p>
                    </div>
                </div>
                <div class="g11c-questions">
                    <span>الفكرة بتعمل إيه؟</span>
                    <span>بنستخدمها فين؟</span>
                    <span>السؤال ممكن ييجي عليها إزاي؟</span>
                    <span>إزاي ترتبط بتطبيق أو نظام حقيقي؟</span>
                </div>
                <p class="g11c-brand-line">المنهج أولًا… والمهارة خطوة بخطوة.</p>
            </div>
        </section>

        <!-- Teacher trust -->
        <section id="teacher" class="g11c-section">
            <div class="g11c-wrap g11c-teacher">
                <div class="g11c-teacher__media">
                    <img
                        :src="teacherImage"
                        alt="كورس البرمجة تانية ثانوي مع محمد غنيم"
                        width="512"
                        height="640"
                        loading="lazy"
                        decoding="async"
                        class="g11c-teacher__img"
                    />
                </div>
                <div class="g11c-teacher__content">
                    <h2 class="g11c-h2">هل تبحث عن مدرس برمجة تانية ثانوي بكالوريا؟</h2>
                    <p class="g11c-text">
                        مع المهندس محمد غنيم، الطالب مش بيحصل على فيديوهات شرح فقط.
                        الطالب يدخل منظومة تساعده على:
                    </p>
                    <ul class="g11c-checklist">
                        <li>فهم الدرس من البداية.</li>
                        <li>ربط المفهوم بأمثلة حقيقية.</li>
                        <li>التدريب على الأسئلة.</li>
                        <li>مراجعة الأخطاء.</li>
                        <li>متابعة مستواه.</li>
                        <li>الاستعداد للامتحان.</li>
                        <li>اكتشاف علاقة المنهج بالبرمجة والذكاء الاصطناعي.</li>
                    </ul>
                    <div class="g11c-cta-pair">
                        <router-link
                            :to="config.aboutGhonaimRoute"
                            class="g11c-btn g11c-btn--secondary"
                            @click.native="onTrack('seo_grade11_about_ghonaim_click', 'teacher_section', config.aboutGhonaimUrl)"
                        >
                            <lucide-icon name="User" :size="20" aria-hidden="true" />
                            <span>تعرف أكثر على محمد غنيم</span>
                        </router-link>
                        <router-link
                            :to="config.introCourseRoute"
                            class="g11c-btn g11c-btn--primary"
                            @click.native="onTrack('seo_grade11_intro_course_click', 'teacher_section', config.introCourseUrl)"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            <span>ابدأ الكورس التمهيدي مجانًا</span>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section id="faq" class="g11c-section g11c-section--soft">
            <div class="g11c-wrap g11c-section__narrow">
                <h2 class="g11c-h2">الأسئلة الشائعة</h2>
                <curriculum-faq-accordion :items="faqItems" @open="onFaqOpen" />
            </div>
        </section>

        <!-- Final CTA -->
        <section id="final-cta" class="g11c-section g11c-section--final">
            <div class="g11c-wrap g11c-section__narrow g11c-final">
                <p class="g11c-kicker g11c-kicker--on-final">ابدأ من مستواك الحالي</p>
                <h2 class="g11c-h2 g11c-h2--light">ابدأ رحلتك في البرمجة والذكاء الاصطناعي</h2>
                <p class="g11c-text g11c-text--light">
                    أنت لا تتعلم فقط كيف تستخدم التكنولوجيا. أنت تبدأ في فهم كيف تُبنى.
                </p>
                <p class="g11c-text g11c-text--light">
                    راجع الأساس مجانًا، أو ابدأ منهج تانية ثانوي من الشهر الأول بخطة واضحة.
                </p>
                <div class="g11c-cta-pair g11c-cta-pair--center">
                    <a
                        href="#downloads"
                        class="g11c-btn g11c-btn--outline-light"
                        @click.prevent="scrollToDownloads('final_cta')"
                    >
                        <lucide-icon name="BookMarked" :size="20" aria-hidden="true" />
                        <span>حمّل كتاب الوزارة</span>
                    </a>
                    <router-link
                        :to="config.introCourseRoute"
                        class="g11c-btn g11c-btn--primary g11c-btn--on-dark"
                        @click.native="onTrack('seo_grade11_intro_course_click', 'final_cta', config.introCourseUrl)"
                    >
                        <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                        <span>ابدأ الكورس التمهيدي مجانًا</span>
                    </router-link>
                    <router-link
                        :to="config.monthOneCourseRoute"
                        class="g11c-btn g11c-btn--outline-light"
                        @click.native="onTrack('seo_grade11_month1_click', 'final_cta', config.monthOneCourseUrl)"
                    >
                        <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                        <span>اشترك في كورس الشهر الأول</span>
                    </router-link>
                </div>
                <p class="g11c-final__brand">
                    Coding With Ghonaim
                    <br />
                    بنفهم التكنولوجيا… مش بس بنستخدمها.
                </p>
            </div>
        </section>

        <curriculum-mobile-sticky
            :intro-route="config.introCourseRoute"
            :month-one-route="config.monthOneCourseRoute"
            @cta="onStickyCta"
        />

        <div
            v-if="downloadSheetOpen"
            class="g11c-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="g11c-download-sheet-title"
            @keydown.esc.prevent="closeDownloadSheet"
        >
            <button type="button" class="g11c-sheet__backdrop" aria-label="إغلاق" @click="closeDownloadSheet" />
            <div class="g11c-sheet__panel" ref="downloadSheet">
                <div class="g11c-sheet__head">
                    <h2 id="g11c-download-sheet-title" class="g11c-sheet__title">اختر نسخة الكتاب</h2>
                    <button type="button" class="g11c-sheet__close" aria-label="إغلاق" @click="closeDownloadSheet">
                        <lucide-icon name="X" :size="20" />
                    </button>
                </div>
                <div class="g11c-sheet__actions">
                    <a
                        v-for="book in downloadBooks"
                        :key="`sheet-${book.id}`"
                        :href="book.url"
                        class="g11c-btn"
                        :class="book.language === 'ar' ? 'g11c-btn--primary' : 'g11c-btn--secondary'"
                        :download="book.fileName"
                        @click="onSheetDownload(book)"
                    >
                        <lucide-icon name="Download" :size="18" aria-hidden="true" />
                        <span>{{ book.sheetLabel }}</span>
                    </a>
                </div>
            </div>
        </div>

        <div
            v-if="mapModalOpen && curriculumMap"
            class="g11c-modal"
            role="dialog"
            aria-modal="true"
            :aria-label="curriculumMap.label"
            @keydown.esc.prevent="closeMapModal"
        >
            <button type="button" class="g11c-modal__backdrop" aria-label="إغلاق" @click="closeMapModal" />
            <div class="g11c-modal__panel" ref="mapModal">
                <button type="button" class="g11c-modal__close" aria-label="إغلاق" @click="closeMapModal">
                    <lucide-icon name="X" :size="22" />
                </button>
                <img
                    :src="curriculumMap.src"
                    :alt="curriculumMap.alt"
                    :title="curriculumMap.title"
                    :width="curriculumMap.width"
                    :height="curriculumMap.height"
                    class="g11c-modal__img"
                />
                <p class="g11c-modal__caption">{{ curriculumMap.caption }}</p>
            </div>
        </div>
    </main>
</template>

<script>
import LucideIcon from '@/components/welcome/landing/LucideIcon.vue';
import CurriculumChapter from './CurriculumChapter.vue';
import CurriculumFaqAccordion from './CurriculumFaqAccordion.vue';
import CurriculumMobileSticky from './CurriculumMobileSticky.vue';
import {
    grade11CurriculumConfig,
    curriculumChapters,
    curriculumFaqItems,
    curriculumBenefits,
    curriculumCareerFields,
    curriculumPrepSteps,
    curriculumSystemFlow,
    curriculumMap,
    journeyBanner,
    curriculumIntroVideo,
    downloadBooks,
    downloadBookGroups,
} from './grade11-curriculum-data';
import { trackSeoCta, trackSeoEvent, trackSeoPageView } from '@/tracking/seo-events';

export default {
    name: 'Grade11CurriculumLanding',
    components: {
        LucideIcon,
        CurriculumChapter,
        CurriculumFaqAccordion,
        CurriculumMobileSticky,
    },
    data() {
        return {
            config: grade11CurriculumConfig,
            chapters: curriculumChapters,
            curriculumMap,
            journeyBanner,
            introVideo: curriculumIntroVideo,
            faqItems: curriculumFaqItems,
            benefits: curriculumBenefits,
            careerFields: curriculumCareerFields,
            prepSteps: curriculumPrepSteps,
            systemFlow: curriculumSystemFlow,
            heroChips: [
                { label: 'كتاب الوزارة PDF', icon: 'BookMarked' },
                { label: 'عربي ولغات', icon: 'Languages' },
                { label: 'جزء نظري وعملي', icon: 'Layers' },
                { label: '7 فصول مترابطة', icon: 'Network' },
                { label: 'Programming & AI', icon: 'Code2' },
                { label: 'شرح وتدريب ومراجعات', icon: 'ClipboardCheck' },
            ],
            overviewPoints: [
                'كيف تعمل الأنظمة الرقمية.',
                'كيف نحمي الحسابات والبيانات.',
                'كيف تعمل المواقع وتطبيقات الويب.',
                'كيف نصمم تجربة سهلة للمستخدم.',
                'كيف نجمع البيانات ونجهزها.',
                'كيف نحلل البيانات ونعرض نتائجها.',
                'كيف تتعلم أنظمة الذكاء الاصطناعي من البيانات.',
            ],
            teacherImage: require('@/assets/welcome/ghonaim-portrait.webp'),
            downloadBooks,
            downloadBookGroups,
            scrollMilestones: { half: false, almost: false },
            mapModalOpen: false,
            downloadSheetOpen: false,
            downloadSheetPlacement: 'downloads',
            previouslyFocused: null,
        };
    },
    computed: {
        youtubeEmbedUrl() {
            const id = this.introVideo && this.introVideo.youtubeId;
            if (!id) return '';
            return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
        },
    },
    mounted() {
        trackSeoPageView('seo_grade11_curriculum_view');
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('keydown', this.onGlobalKeydown);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('keydown', this.onGlobalKeydown);
        this.unlockBodyScroll();
    },
    methods: {
        onTrack(eventName, placement, destination) {
            trackSeoCta(eventName, placement, destination);
        },
        onInternal(kind, placement) {
            if (kind === 'home') {
                trackSeoEvent('seo_grade11_curriculum_view', { placement });
            }
        },
        onChapterNav(chapter) {
            trackSeoEvent('seo_grade11_chapter_navigation_click', {
                placement: 'toc',
                destination: `#${chapter.id}`,
            });
        },
        scrollToChapters() {
            trackSeoEvent('seo_grade11_journey_banner_click', {
                placement: 'journey_banner',
                destination: '#chapters',
            });
            const el = document.getElementById('chapters');
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                this.$router.replace({ hash: '#chapters' }).catch(() => {});
            }
        },
        onFaqOpen({ question }) {
            trackSeoEvent('seo_grade11_faq_open', { placement: 'faq', destination: question });
        },
        onStickyCta(kind) {
            if (kind === 'download') {
                this.openDownloadSheet('mobile_sticky');
                return;
            }
            if (kind === 'intro') {
                this.onTrack('seo_grade11_intro_course_click', 'mobile_sticky', this.config.introCourseUrl);
            } else {
                this.onTrack('seo_grade11_month1_click', 'mobile_sticky', this.config.monthOneCourseUrl);
            }
        },
        onBookTrack(eventName, placement, destination) {
            trackSeoEvent(eventName, { placement, destination });
        },
        scrollToDownloads(placement) {
            trackSeoEvent('seo_grade11_book_section_click', {
                placement,
                destination: '#downloads',
            });
            const el = document.getElementById('downloads');
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                this.$router.replace({ hash: '#downloads' }).catch(() => {});
            }
        },
        openDownloadSheet(placement) {
            this.previouslyFocused = document.activeElement;
            this.downloadSheetPlacement = placement || 'downloads';
            this.downloadSheetOpen = true;
            this.lockBodyScroll();
            this.$nextTick(() => {
                const panel = this.$refs.downloadSheet;
                if (panel && panel.querySelector) {
                    const focusable = panel.querySelector('button, a');
                    if (focusable) focusable.focus();
                }
            });
            trackSeoEvent('seo_grade11_download_sheet_open', {
                placement: this.downloadSheetPlacement,
            });
        },
        closeDownloadSheet() {
            this.downloadSheetOpen = false;
            this.unlockBodyScroll();
            if (this.previouslyFocused && this.previouslyFocused.focus) {
                this.previouslyFocused.focus();
            }
        },
        onSheetDownload(book) {
            this.onBookTrack(book.trackDownload, this.downloadSheetPlacement, book.url);
            this.closeDownloadSheet();
        },
        openMapModal() {
            this.previouslyFocused = document.activeElement;
            this.mapModalOpen = true;
            this.lockBodyScroll();
            this.$nextTick(() => {
                const panel = this.$refs.mapModal;
                if (panel && panel.querySelector) {
                    const focusable = panel.querySelector('button, a');
                    if (focusable) focusable.focus();
                }
            });
            trackSeoEvent('seo_grade11_curriculum_map_open', {
                placement: 'chapters',
                destination: this.curriculumMap.id,
            });
        },
        closeMapModal() {
            this.mapModalOpen = false;
            this.unlockBodyScroll();
            if (this.previouslyFocused && this.previouslyFocused.focus) {
                this.previouslyFocused.focus();
            }
        },
        onGlobalKeydown(event) {
            if (event.key !== 'Escape') {
                return;
            }
            if (this.downloadSheetOpen) {
                this.closeDownloadSheet();
            } else if (this.mapModalOpen) {
                this.closeMapModal();
            }
        },
        lockBodyScroll() {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        },
        unlockBodyScroll() {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        },
        onScroll() {
            const doc = document.documentElement;
            const max = doc.scrollHeight - window.innerHeight;
            if (max <= 0) {
                return;
            }
            const ratio = window.scrollY / max;
            if (!this.scrollMilestones.half && ratio >= 0.5) {
                this.scrollMilestones.half = true;
                trackSeoEvent('seo_grade11_scroll_50');
            }
            if (!this.scrollMilestones.almost && ratio >= 0.9) {
                this.scrollMilestones.almost = true;
                trackSeoEvent('seo_grade11_scroll_90');
            }
        },
    },
};
</script>
